/**
 * overlayStack.ts — geraeteuebergreifende LIFO-Verwaltung aller Overlay-Ebenen.
 *
 * EINE zentrale Wahrheit, welche Overlay-Ebene gerade „oben" liegt — egal welchen
 * Typs (Dialog, Popover, kuenftig ContextMenu/Inspector). Damit schliessen Esc
 * und die Android-Hardware-Back-Taste IMMER genau die OBERSTE Ebene, auch bei
 * gemischten Typen (Popover ueber Dialog). Bisher trackten DialogBase und Popover
 * ihre „bin ich oben?"-Frage je getrennt → ein Popover ueber einem Dialog schloss
 * bei Esc beide (cross-type-Luecke).
 *
 * Bewusst KEIN keydown-Listener hier: Esc laeuft weiter ueber die Overlay-
 * Komponenten selbst (bzw. spaeter die Registry), gegated auf peekTop().
 * Die Hardware-Back-Andockung passiert dagegen GENAU EINMAL fuer den ganzen
 * Stapel (nicht pro Overlay).
 */

export interface OverlayEntry {
  id: string;
  close: () => void;
}

// ---------------------------------------------------------------------------
// Bruecke zum zentralen Back-Handler (window.LorathBackHandler → native/bridge.ts)
// ---------------------------------------------------------------------------

interface LorathBackHandlerBridge {
  register?: (handler: () => boolean) => (() => void) | void;
  add?: (handler: () => boolean) => void;
  remove?: (handler: () => boolean) => void;
  push?: (handler: () => boolean) => (() => void) | void;
}

declare global {
  interface Window {
    LorathBackHandler?: LorathBackHandlerBridge;
  }
}

// ---------------------------------------------------------------------------
// Modul-State: LIFO-Stapel + Abonnenten
// ---------------------------------------------------------------------------

const stack: OverlayEntry[] = [];
const subscribers = new Set<() => void>();

// Freigabe der EINMALIGEN Hardware-Back-Andockung. undefined = nicht angedockt.
let releaseBackBridge: (() => void) | undefined;

function notify(): void {
  for (const cb of subscribers) cb();
}

/**
 * Dockt den zentralen Back-Handler GENAU EINMAL an (beim ersten push). Der
 * Handler ruft closeTopOverlay() und meldet true zurueck (Back verbraucht).
 * Bevorzugt bridge.register (liefert Cleanup), sonst push, sonst add/remove.
 */
function attachBackBridge(): void {
  if (releaseBackBridge || typeof window === 'undefined') return;
  const bridge = window.LorathBackHandler;
  if (!bridge) return;

  const handler = (): boolean => {
    // E4a-Verify-MAJOR: bridge.ts popCloser ENTFERNT den Handler beim Feuern
    // (pop-once) — diese Registrierung ist mit dem Aufruf VERBRAUCHT. Zustand
    // sofort nachziehen und bei verbleibenden Ebenen NEU andocken, sonst fiele
    // der zweite Back-Druck auf history.back()/exitApp durch, waehrend der
    // Dialog darunter noch offen ist. Das cleanup vorher aufzurufen ist bei
    // pop-once ein harmloser No-op und schuetzt zugleich eine hypothetisch
    // persistente Bridge vor Doppel-Registrierung.
    releaseBackBridge?.();
    releaseBackBridge = undefined;
    closeTopOverlay();
    if (stack.length > 0) attachBackBridge();
    return true;
  };

  if (bridge.register) {
    const cleanup = bridge.register(handler);
    releaseBackBridge = typeof cleanup === 'function'
      ? cleanup
      : () => bridge.remove?.(handler);
  } else if (bridge.push) {
    const cleanup = bridge.push(handler);
    releaseBackBridge = typeof cleanup === 'function'
      ? cleanup
      : () => bridge.remove?.(handler);
  } else if (bridge.add) {
    bridge.add(handler);
    releaseBackBridge = () => bridge.remove?.(handler);
  }
}

/** Loest die Hardware-Back-Andockung, sobald der letzte Eintrag gepoppt ist. */
function detachBackBridgeIfEmpty(): void {
  if (stack.length > 0) return;
  releaseBackBridge?.();
  releaseBackBridge = undefined;
}

// ---------------------------------------------------------------------------
// Oeffentliche API
// ---------------------------------------------------------------------------

/**
 * Legt einen Overlay-Eintrag oben auf den Stapel. Gibt einen idempotenten
 * unregister zurueck (Splice per Identitaet — mehrfaches Aufrufen ist sicher).
 */
export function pushOverlay(entry: OverlayEntry): () => void {
  stack.push(entry);
  if (stack.length === 1) attachBackBridge();
  notify();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    const index = stack.lastIndexOf(entry);
    if (index >= 0) stack.splice(index, 1);
    detachBackBridgeIfEmpty();
    notify();
  };
}

/** Aktuelle Tiefe des Overlay-Stapels. */
export function getOverlayDepth(): number {
  return stack.length;
}

/** Oberster Eintrag (ohne ihn zu poppen) oder undefined bei leerem Stapel. */
export function peekTop(): OverlayEntry | undefined {
  return stack[stack.length - 1];
}

/**
 * Schliesst die oberste Overlay-Ebene (ruft deren close()). Poppt den Eintrag
 * NICHT selbst — das Overlay meldet sein Schliessen ueber den von pushOverlay
 * zurueckgegebenen unregister zurueck. Gibt true zurueck, wenn es etwas zu
 * schliessen gab.
 */
export function closeTopOverlay(): boolean {
  const top = peekTop();
  if (!top) return false;
  top.close();
  return true;
}

/**
 * Abonniert push/pop-Aenderungen (fuer reaktives overlayDepth, Paket A).
 * Gibt eine Abmelde-Funktion zurueck.
 */
export function subscribeOverlay(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}
